import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, ChevronRight, ChevronLeft, X, Menu, Clock, Tag, Eye, Timer, Sparkles } from 'lucide-react';
import menuJson from './menu.json';

const formatTime = (time24) => {
  const [hour, minute] = time24.split(':').map(Number);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
};

const getNextHappyHour = (currentTime, schedule) => {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const currentDayIndex = currentTime.getDay();

  for (let i = 0; i < 7; i++) {
    const checkDayIndex = (currentDayIndex + i) % 7;
    const checkDay = dayNames[checkDayIndex];
    const daySchedule = schedule.find(s => s.day_of_week === checkDay);

    if (daySchedule) {
      if (i === 0) {
        const [startHour, startMinute] = daySchedule.start_time.split(':').map(Number);
        const currentHour = currentTime.getHours();
        const currentMinute = currentTime.getMinutes();

        if (currentHour < startHour || (currentHour === startHour && currentMinute < startMinute)) {
          return `Today at ${formatTime(daySchedule.start_time)}`;
        }
      } else if (i === 1) {
        return `Tomorrow at ${formatTime(daySchedule.start_time)}`;
      } else {
        return `${checkDay} at ${formatTime(daySchedule.start_time)}`;
      }
    }
  }

  return '';
};

const TabacMenuInterface = () => 
{
  const [menuData, setMenuData] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [activeProduct, setActiveProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('categories');
  const [allProducts, setAllProducts] = useState([]);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [productPositions, setProductPositions] = useState({});
  const [previousView, setPreviousView] = useState(null);
  const [initialUrlProcessed, setInitialUrlProcessed] = useState(false);
  const [allViewActiveCategory, setAllViewActiveCategory] = useState(null);
  const [filteredProductsByCategory, setFilteredProductsByCategory] = useState([]);
  const [happyHourStatus, setHappyHourStatus] = useState({ isActive: false, timeRemaining: '', nextStart: '' });
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const menuButtonRef = useRef(null);
  const searchButtonRef = useRef(null);
  const sidebarFirstActionRef = useRef(null);
  const observerRef = useRef(null);
  const loaderRef = useRef(null);
  const productRefs = useRef({});
  const subcategoryRefs = useRef({});
  const imageCache = useRef(new Map());
  const preloadedImages = useRef(new Set());
  const initialUrl = useRef(window.location.pathname);
  const processedProductIds = useRef(new Set());

  const PRODUCTS_PER_LOAD = 10;
  const ACTIVE_BADGE_CLASS = 'bg-[#14532d] text-white px-3 py-1 rounded-full text-sm font-medium';

  const calculateHappyHourStatus = useCallback(() => 
  {
    if (!menuData?.happyHour?.enabled) 
    {
      return { isActive: false, timeRemaining: '', nextStart: '' };
    }

    const now = new Date();
    const losAngelesTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
    
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDay = dayNames[losAngelesTime.getDay()];
    const currentHour = losAngelesTime.getHours();
    const currentMinute = losAngelesTime.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    
    const schedule = menuData.happyHour.schedule || [];
    const todaySchedule = schedule.find(s => s.day_of_week === currentDay);
    
    if (todaySchedule) 
    {
      const [startHour, startMinute] = todaySchedule.start_time.split(':').map(Number);
      const [endHour, endMinute] = todaySchedule.end_time.split(':').map(Number);
      const startTimeInMinutes = startHour * 60 + startMinute;
      const endTimeInMinutes = endHour * 60 + endMinute;
      
      if (currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes < endTimeInMinutes) 
      {
        const remainingMinutes = endTimeInMinutes - currentTimeInMinutes;
        const hours = Math.floor(remainingMinutes / 60);
        const minutes = remainingMinutes % 60;
        
        return {
          isActive: true,
          timeRemaining: `${hours}h ${minutes}m remaining`,
          countdown: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
          nextStart: ''
        };
      }
    }
    
    const nextHappyHour = getNextHappyHour(losAngelesTime, schedule);
    
    return {
      isActive: false,
      timeRemaining: '',
      nextStart: nextHappyHour,
      displayText: menuData.happyHour.displayText || 'Monday - Thursday | 5:00 PM - 7:00 PM'
    };
  }, [menuData]);

  useEffect(() => 
  {
    const interval = setInterval(() => 
    {
      setHappyHourStatus(calculateHappyHourStatus());
    }, 60000);
    
    setHappyHourStatus(calculateHappyHourStatus());
    
    return () => clearInterval(interval);
  }, [calculateHappyHourStatus]);

  const HappyHourCard = ({ compact = false }) => 
  {
    if (!menuData?.happyHour?.enabled) return null;
    
    const hasHappyHourProducts = menuData.categories?.some(cat => 
      cat.id === 'happy-hour' && cat.subcategories?.some(sub => sub.products?.length > 0)
    );
    
    if (!hasHappyHourProducts) return null;
    
    if (compact) 
    {
      return (
        <button
          type="button"
          className="w-full text-left bg-gradient-to-r from-[#4d1010] to-[#7a1c1c] rounded-lg p-4 cursor-pointer hover:shadow-xl transition-all duration-300 border border-[#7a1c1c]"
          onClick={() => 
          {
            const happyHourCategory = menuData.categories.find(c => c.id === 'happy-hour');
            if (happyHourCategory) 
            {
              handleCategoryClick(happyHourCategory);
            }
          }}
          aria-label="View Happy Hour specials"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Sparkles className="mr-2 text-yellow-400" size={20} />
              <span className="font-medium text-white">Happy Hour</span>
            </div>
            {happyHourStatus.isActive ? (
              <div className="flex items-center">
                <span className="text-xs bg-[#14532d] text-white px-2 py-1 rounded-full mr-2">NOW ACTIVE!</span>
                <Timer size={16} className="text-yellow-400 mr-1" />
                <span className="text-sm text-yellow-400">{happyHourStatus.countdown}</span>
              </div>
            ) : (
              <span className="text-xs text-[#e9d2d2]">{happyHourStatus.displayText}</span>
            )}
          </div>
        </button>
      );
    }
    
    return (
      <div className="bg-gradient-to-r from-[#4d1010] to-[#7a1c1c] rounded-xl p-6 mb-8 border border-[#7a1c1c] shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Sparkles className="mr-3 text-yellow-400" size={28} />
            <h2 className="text-2xl font-light tracking-wider text-white">Happy Hour Specials</h2>
          </div>
          {happyHourStatus.isActive && (
            <div className={`${ACTIVE_BADGE_CLASS} animate-pulse`}>
              NOW ACTIVE!
            </div>
          )}
        </div>
        
        {happyHourStatus.isActive ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center text-yellow-400">
              <Timer size={20} className="mr-2" />
              <span className="text-lg">{happyHourStatus.timeRemaining}</span>
            </div>
            <div className="text-3xl font-mono text-yellow-400">
              {happyHourStatus.countdown}
            </div>
          </div>
        ) : (
          <div>
            <div className="text-[#e9d2d2] mb-2">
              <Clock size={16} className="inline mr-2" />
              {happyHourStatus.displayText}
            </div>
            {happyHourStatus.nextStart && (
              <div className="text-sm text-[#c28f8f]">
                Next Happy Hour: {happyHourStatus.nextStart}
              </div>
            )}
          </div>
        )}
        
        <button 
          onClick={() => 
          {
            const happyHourCategory = menuData.categories.find(c => c.id === 'happy-hour');
            if (happyHourCategory) 
            {
              handleCategoryClick(happyHourCategory);
            }
          }}
          className="mt-4 w-full bg-[#200202] hover:bg-[#300404] text-white py-3 rounded-lg transition-colors duration-200 border border-[#4d1010] hover:border-[#7a1c1c]"
        >
          View Happy Hour Menu
        </button>
      </div>
    );
  };

  const formatPrice = (price, perPerson, short = false) =>
  {
    if (perPerson === 1)
    {
      return short ? `$${price} PP` : `$${price} Per Person`;
    }
    return `$${price}`;
  };

  const ProductPriceDisplay = ({ product, short = false }) =>
  {
    const isHappyHour = product.isHappyHour && product.happyHourPrice;

    if (!isHappyHour)
    {
      return <span className="text-xl font-light text-white">{formatPrice(product.price, product.perPerson, short)}</span>;
    }

    if (happyHourStatus.isActive)
    {
      return (
        <div className="flex flex-col items-end">
          <span className="text-sm line-through text-[#a07676]">{formatPrice(product.price, product.perPerson, true)}</span>
          <span className="text-xl font-light text-yellow-400">{formatPrice(product.happyHourPrice, product.perPerson, short)}</span>
          <span className="text-xs bg-[#14532d] text-white px-2 py-0.5 rounded-full mt-1">HH Price</span>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-end">
        <span className="text-xl font-light text-white">{formatPrice(product.price, product.perPerson, short)}</span>
        <span className="text-sm text-[#c28f8f]">HH: {formatPrice(product.happyHourPrice, product.perPerson, true)}</span>
      </div>
    );
  };

  useEffect(() =>
  {
    if (!menuData) return;

    const handlePopState = () =>
    {
      const path = window.location.pathname;
      const pathParts = path.split('/').filter(Boolean);

      if (pathParts.length === 0)
      {
        setActiveCategory(null);
        setActiveSubcategory(null);
        setActiveProduct(null);
        setView('categories');
        return;
      }

      if (pathParts[0] === 'browse-all')
      {
        setActiveProduct(null);
        setView('all-products');
        if (menuData.categories && menuData.categories.length > 0)
        {
          const firstNonHappyHour = menuData.categories.find(c => c.id !== 'happy-hour') || menuData.categories[0];
          setAllViewActiveCategory(firstNonHappyHour);
        }
        return;
      }

      if (pathParts[0] === 'happy-hour')
      {
        const happyHourCategory = menuData.categories.find(c => c.id === 'happy-hour');
        if (happyHourCategory)
        {
          setActiveCategory(happyHourCategory);

          if (pathParts.length >= 3)
          {
            const subcategoryId = pathParts[1];
            const productId = pathParts[2];
            const subcategory = happyHourCategory.subcategories?.find(s => s.id === subcategoryId);
            if (subcategory)
            {
              setActiveSubcategory(subcategory);
              setView('products');
              const product = subcategory.products?.find(p => p.id === productId);
              if (product)
              {
                const formattedProduct = {
                  ...product,
                  name: product.name.replace(/&amp;/g, '&'),
                  categoryId: happyHourCategory.id,
                  subcategoryId: subcategory.id,
                  category: happyHourCategory,
                  subcategory
                };
                setActiveProduct(formattedProduct);
              }
              else
              {
                setActiveProduct(null);
              }
            }
          }
          else if (pathParts.length >= 2)
          {
            const subcategoryId = pathParts[1];
            const subcategory = happyHourCategory.subcategories?.find(s => s.id === subcategoryId);
            if (subcategory)
            {
              setActiveSubcategory(subcategory);
              setActiveProduct(null);
              setView('products');
            }
            else
            {
              setActiveSubcategory(null);
              setActiveProduct(null);
              setView('subcategories');
            }
          }
          else
          {
            setActiveSubcategory(null);
            setActiveProduct(null);
            setView('subcategories');
          }
        }
        return;
      }

      const categoryId = pathParts[0];
      const category = menuData.categories?.find(c => c.id === categoryId);

      if (category)
      {
        setActiveCategory(category);

        if (pathParts.length >= 3)
        {
          const subcategoryId = pathParts[1];
          const productId = pathParts[2];
          const subcategory = category.subcategories?.find(s => s.id === subcategoryId);
          if (subcategory)
          {
            setActiveSubcategory(subcategory);
            setView('products');
            const product = subcategory.products?.find(p => p.id === productId);
            if (product)
            {
              const formattedProduct = {
                ...product,
                name: product.name.replace(/&amp;/g, '&'),
                categoryName: category.name,
                categoryId: category.id,
                subcategoryName: subcategory.name,
                subcategoryId: subcategory.id,
                category,
                subcategory
              };
              setActiveProduct(formattedProduct);
            }
            else
            {
              setActiveProduct(null);
            }
          }
        }
        else if (pathParts.length >= 2)
        {
          const subcategoryId = pathParts[1];
          const subcategory = category.subcategories?.find(s => s.id === subcategoryId);
          if (subcategory)
          {
            setActiveSubcategory(subcategory);
            setActiveProduct(null);
            setView('products');
          }
          else
          {
            setActiveSubcategory(null);
            setActiveProduct(null);
            setView('subcategories');
          }
        }
        else
        {
          setActiveSubcategory(null);
          setActiveProduct(null);
          setView('subcategories');
        }
      }
      else
      {
        setActiveCategory(null);
        setActiveSubcategory(null);
        setActiveProduct(null);
        setView('categories');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [menuData]);

  useEffect(() =>
  {
    if (!initialUrlProcessed) return;

    let newPath = '/';
    
    if (view === 'all-products') 
    {
      newPath = '/browse-all';
    } 
    else if (activeCategory && activeCategory.id === 'happy-hour') 
    {
      newPath = '/happy-hour';
      if (activeSubcategory) 
      {
        newPath += `/${activeSubcategory.id}`;
      }
    } 
    else if (view === 'subcategories' && activeCategory) 
    {
      newPath = `/${activeCategory.id}`;
    } 
    else if (view === 'products' && activeCategory && activeSubcategory) 
    {
      newPath = `/${activeCategory.id}/${activeSubcategory.id}`;
    }
    
    if (activeProduct) 
    {
      if (activeCategory && activeCategory.id === 'happy-hour') 
      {
        newPath = `/happy-hour/${activeProduct.subcategoryId}/${activeProduct.id}`;
      } 
      else 
      {
        newPath = `/${activeProduct.categoryId}/${activeProduct.subcategoryId}/${activeProduct.id}`;
      }
    }
    
    window.history.pushState({}, '', newPath);
  }, [view, activeCategory, activeSubcategory, activeProduct, initialUrlProcessed]);

  const preloadImage = (src) => 
  {
    if (!src || preloadedImages.current.has(src)) return Promise.resolve();
    
    return new Promise((resolve) => 
    {
      const img = new Image();
      img.onload = () => 
      {
        preloadedImages.current.add(src);
        imageCache.current.set(src, true);
        resolve();
      };
      img.onerror = () => 
      {
        resolve();
      };
      img.src = src;
    });
  };

  const LazyImage = ({ src, alt, className, width = 400, height = 300, objectFit = "cover", ...props }) => 
  {
    const [loaded, setLoaded] = useState(preloadedImages.current.has(src));
    const [error, setError] = useState(false);
    const imgRef = useRef(null);

    useEffect(() => 
    {
      if (!src) 
      {
        setError(true);
        return;
      }

      if (preloadedImages.current.has(src)) 
      {
        setLoaded(true);
        return;
      }

      preloadImage(src).then(() => 
      {
        setLoaded(true);
      }).catch(() => 
      {
        setError(true);
      });
    }, [src]);

    if (!src || error) 
    {
      return (
        <div className={className} {...props}>
          <div 
            className="bg-gradient-to-br from-[#3d0a0a] to-[#200202] flex items-center justify-center text-gray-400 text-xs font-light border border-[#4d1010] w-full h-full"
            style={{ minHeight: height }}
          >
            <div className="text-center">
              <div className="opacity-50 mb-1">
                <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="text-xs opacity-70">{`${width}×${height}`}</div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`${className} relative overflow-hidden`} ref={imgRef} {...props}>
        {!loaded && (
          <div 
            className="absolute inset-0 bg-gradient-to-br from-[#3d0a0a] to-[#200202] flex items-center justify-center text-gray-400 text-xs font-light border border-[#4d1010]"
          >
            <div className="text-center">
              <div className="opacity-50 mb-1">
                <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="text-xs opacity-70">{`${width}×${height}`}</div>
            </div>
          </div>
        )}
        <img
          src={src}
          alt={alt || ""}
          className={`w-full h-full transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ objectFit }}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      </div>
    );
  };

  useEffect(() => 
  {
    const loadMenuData = () => 
    {
      try 
      {
        setLoading(true);
        setMenuData(menuJson);
      } 
      catch (err) 
      {
        console.error('Error loading menu:', err);
        setError('Failed to load menu data.');
      } 
      finally 
      {
        setLoading(false);
      }
    };

    setTimeout(loadMenuData, 500);
  }, []);

  useEffect(() => 
  {
    if (!menuData || initialUrlProcessed) return;
    
    const path = initialUrl.current;
    const pathParts = path.split('/').filter(Boolean);
    
    if (pathParts.length === 0) 
    {
      setView('categories');
      setInitialUrlProcessed(true);
      return;
    }
    
    if (pathParts[0] === 'browse-all') 
    {
      setView('all-products');
      if (menuData.categories && menuData.categories.length > 0) 
      {
        const firstNonHappyHour = menuData.categories.find(c => c.id !== 'happy-hour') || menuData.categories[0];
        setAllViewActiveCategory(firstNonHappyHour);
      }
      setInitialUrlProcessed(true);
      return;
    }
    
    if (pathParts[0] === 'happy-hour') 
    {
      const happyHourCategory = menuData.categories.find(c => c.id === 'happy-hour');
      if (happyHourCategory) 
      {
        setActiveCategory(happyHourCategory);
        setView('subcategories');
        
        if (pathParts.length >= 2) 
        {
          const subcategoryId = pathParts[1];
          const subcategory = happyHourCategory.subcategories.find(s => s.id === subcategoryId);
          
          if (subcategory) 
          {
            setActiveSubcategory(subcategory);
            setView('products');
            
            if (pathParts.length >= 3) 
            {
              const productId = pathParts[2];
              const product = subcategory.products.find(p => p.id === productId);
              
              if (product) 
              {
                const formattedProduct = {
                  ...product,
                  name: product.name.replace(/&amp;/g, '&'),
                  category: happyHourCategory,
                  subcategory
                };
                setActiveProduct(formattedProduct);
              }
            }
          }
        }
      }
      setInitialUrlProcessed(true);
      return;
    }

    const categoryId = pathParts[0];
    const category = menuData.categories.find(c => c.id === categoryId);
    
    if (category) 
    {
      setActiveCategory(category);
      
      if (pathParts.length >= 2) 
      {
        const subcategoryId = pathParts[1];
        const subcategory = category.subcategories.find(s => s.id === subcategoryId);
        
        if (subcategory) 
        {
          setActiveSubcategory(subcategory);
          setView('products');
          
          if (pathParts.length >= 3) 
          {
            const productId = pathParts[2];
            const product = subcategory.products.find(p => p.id === productId);
            
            if (product) 
            {
              const formattedProduct = {
                ...product,
                name: product.name.replace(/&amp;/g, '&'),
                categoryName: category.name,
                categoryId: category.id,
                subcategoryName: subcategory.name,
                subcategoryId: subcategory.id,
                category,
                subcategory
              };
              setActiveProduct(formattedProduct);
            }
          }
        }
      } 
      else 
      {
        setView('subcategories');
      }
    }
    
    setInitialUrlProcessed(true);
  }, [menuData, initialUrlProcessed]);

  useEffect(() => 
  {
    if (!menuData?.categories) return;
    
    const products = [];
    processedProductIds.current.clear();
    
    menuData.categories.forEach(category => 
    {
      if (category.id === 'happy-hour') return;
      
      category.subcategories?.forEach(subcategory => 
      {
        subcategory.products?.forEach(product => 
        {
          if (!processedProductIds.current.has(product.id)) 
          {
            processedProductIds.current.add(product.id);
            products.push({
              ...product,
              name: product.name.replace(/&amp;/g, '&'),
              categoryName: category.name,
              categoryId: category.id,
              subcategoryName: subcategory.name,
              subcategoryId: subcategory.id,
              category,
              subcategory
            });
          }
        });
      });
    });
    setAllProducts(products);
    
    if (menuData.categories.length > 0 && view === 'all-products') 
    {
      const firstNonHappyHour = menuData.categories.find(c => c.id !== 'happy-hour') || menuData.categories[0];
      setAllViewActiveCategory(firstNonHappyHour);
    }
  }, [menuData, view]);

  useEffect(() => 
  {
    if (!allViewActiveCategory || !allProducts.length) return;
    
    const filteredProducts = allProducts.filter(product => 
      product.categoryId === allViewActiveCategory.id
    );
    
    setFilteredProductsByCategory(filteredProducts);
  }, [allViewActiveCategory, allProducts]);

  useEffect(() => 
  {
    const preloadVisibleImages = async () => 
    {
      if (visibleProducts && visibleProducts.length > 0) 
      {
        const imagesToPreload = visibleProducts
          .filter(product => product.image)
          .map(product => product.image);
        
        const chunks = [];
        for (let i = 0; i < imagesToPreload.length; i += 3) 
        {
          chunks.push(imagesToPreload.slice(i, i + 3));
        }
        
        for (const chunk of chunks) 
        {
          await Promise.all(chunk.map(src => preloadImage(src)));
        }
      }
    };
    
    preloadVisibleImages();
  }, [visibleProducts]);

  useEffect(() => 
  {
    const handleScroll = () => 
    {
      setScrollPosition(window.pageYOffset);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => 
    {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => 
  {
    if (view === 'all-products') 
    {
      setVisibleProducts(filteredProductsByCategory.slice(0, PRODUCTS_PER_LOAD));
      setCurrentIndex(PRODUCTS_PER_LOAD);
      setHasMore(filteredProductsByCategory.length > PRODUCTS_PER_LOAD);
    } 
    else if (view === 'products' && activeSubcategory) 
    {
      const processedIds = new Set();
      const products = [];
      
      if (activeSubcategory.products) 
      {
        activeSubcategory.products.forEach(product => 
        {
          if (!processedIds.has(product.id)) 
          {
            processedIds.add(product.id);
            products.push({
              ...product,
              name: product.name.replace(/&amp;/g, '&'),
              categoryName: activeCategory.name,
              categoryId: activeCategory.id,
              subcategoryName: activeSubcategory.name,
              subcategoryId: activeSubcategory.id,
              category: activeCategory,
              subcategory: activeSubcategory
            });
          }
        });
      }
      
      setVisibleProducts(products.slice(0, PRODUCTS_PER_LOAD));
      setCurrentIndex(PRODUCTS_PER_LOAD);
      setHasMore(products.length > PRODUCTS_PER_LOAD);
    }
  }, [view, activeSubcategory, activeCategory, allProducts, filteredProductsByCategory]);

  useEffect(() => 
  {
    const newProductRefs = {};
    visibleProducts.forEach(product => 
    {
      if (!productRefs.current[product.id]) 
      {
        newProductRefs[product.id] = React.createRef();
      }
    });
    productRefs.current = { ...productRefs.current, ...newProductRefs };
    
    if (allViewActiveCategory && allViewActiveCategory.subcategories) 
    {
      const newSubcategoryRefs = {};
      allViewActiveCategory.subcategories.forEach(subcategory => 
      {
        if (!subcategoryRefs.current[subcategory.id]) 
        {
          newSubcategoryRefs[subcategory.id] = React.createRef();
        }
      });
      subcategoryRefs.current = { ...subcategoryRefs.current, ...newSubcategoryRefs };
    }
  }, [visibleProducts, allViewActiveCategory]);

  const loadMoreProducts = useCallback(() => 
  {
    if (!hasMore || activeProduct) return;
    
    if (view === 'all-products') 
    {
      const nextProducts = filteredProductsByCategory.slice(currentIndex, currentIndex + PRODUCTS_PER_LOAD);
      if (nextProducts.length > 0) 
      {
        setVisibleProducts(prev => [...prev, ...nextProducts]);
        setCurrentIndex(prev => prev + PRODUCTS_PER_LOAD);
        setHasMore(currentIndex + PRODUCTS_PER_LOAD < filteredProductsByCategory.length);
      } 
      else 
      {
        setHasMore(false);
      }
    } 
    else if (view === 'products' && activeSubcategory) 
    {
      const processedIds = new Set(visibleProducts.map(p => p.id));
      const remainingProducts = [];
      
      if (activeSubcategory.products) 
      {
        activeSubcategory.products.forEach(product => 
        {
          if (!processedIds.has(product.id)) 
          {
            processedIds.add(product.id);
            remainingProducts.push({
              ...product,
              name: product.name.replace(/&amp;/g, '&'),
              categoryName: activeCategory.name,
              categoryId: activeCategory.id,
              subcategoryName: activeSubcategory.name,
              subcategoryId: activeSubcategory.id,
              category: activeCategory,
              subcategory: activeSubcategory
            });
          }
        });
      }
      
      const nextProducts = remainingProducts.slice(0, PRODUCTS_PER_LOAD);
      if (nextProducts.length > 0) 
      {
        setVisibleProducts(prev => [...prev, ...nextProducts]);
        setCurrentIndex(prev => prev + PRODUCTS_PER_LOAD);
        setHasMore(remainingProducts.length > PRODUCTS_PER_LOAD);
      } 
      else 
      {
        setHasMore(false);
      }
    }
  }, [view, currentIndex, filteredProductsByCategory, activeSubcategory, activeCategory, hasMore, activeProduct, visibleProducts]);

  useEffect(() => 
  {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => 
      {
        if (entries[0].isIntersecting && hasMore && !activeProduct) 
        {
          loadMoreProducts();
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    );

    if (loaderRef.current && hasMore && !activeProduct) 
    {
      observerRef.current.observe(loaderRef.current);
    }

    return () => 
    {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [loadMoreProducts, hasMore, activeProduct]);

  useEffect(() => 
  {
    if (searchQuery.length > 1 && menuData?.categories) 
    {
      const results = [];
      const processedIds = new Set();
      
      menuData.categories.forEach(category => 
      {
        category.subcategories?.forEach(subcategory => 
        {
          const filteredProducts = subcategory.products?.filter(product => 
          {
            const description = typeof product.description === 'string' 
              ? product.description 
              : product.description?.replace(/<[^>]*>/g, '') || '';
            
            return product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                   description.toLowerCase().includes(searchQuery.toLowerCase());
          }) || [];
          
          filteredProducts.forEach(product => 
          {
            if (!processedIds.has(product.id)) 
            {
              processedIds.add(product.id);
              results.push({
                ...product,
                name: product.name.replace(/&amp;/g, '&'),
                categoryName: category.name,
                categoryId: category.id,
                subcategoryName: subcategory.name,
                subcategoryId: subcategory.id,
                category,
                subcategory
              });
            }
          });
        });
      });
      
      setSearchResults(results);
    } 
    else 
    {
      setSearchResults([]);
    }
  }, [searchQuery, menuData]);

  useEffect(() => 
  {
    const handleClickOutside = (event) => 
    {
      if (menuRef.current && !menuRef.current.contains(event.target)) 
      {
        setMenuOpen(false);
      }
      
      if (searchRef.current && !searchRef.current.contains(event.target)) 
      {
        if (searchOpen && searchQuery === '') 
        {
          setSearchOpen(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => 
    {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef, searchRef, searchOpen, searchQuery]);

  useEffect(() => 
  {
    if (searchOpen && searchInputRef.current) 
    {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() =>
  {
    if (menuOpen && sidebarFirstActionRef.current)
    {
      sidebarFirstActionRef.current.focus();
    }
  }, [menuOpen]);

  useEffect(() =>
  {
    const handleEscape = (event) =>
    {
      if (event.key === 'Escape')
      {
        if (searchOpen)
        {
          setSearchOpen(false);
          searchButtonRef.current?.focus();
        }

        if (menuOpen)
        {
          setMenuOpen(false);
          menuButtonRef.current?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [menuOpen, searchOpen]);

  const handleCategoryClick = (category) =>
  {
    setMenuOpen(false);
    setActiveCategory(category);
    if (category.subcategories?.length === 1)
    {
      setActiveSubcategory(category.subcategories[0]);
      setView('products');
    }
    else
    {
      setView('subcategories');
    }
    window.scrollTo(0, 0);
  };

  const handleSubcategoryClick = (subcategory) => 
  {
    setMenuOpen(false);
    setActiveSubcategory(subcategory);
    setView('products');
    window.scrollTo(0, 0);
  };

  const handleAllViewCategoryClick = (category) => 
  {
    setMenuOpen(false);
    setAllViewActiveCategory(category);
    setVisibleProducts([]);
    setCurrentIndex(0);
    setTimeout(() => 
    {
      window.scrollTo(0, 0);
    }, 0);
  };

  const scrollToSubcategory = (subcategoryId) => 
  {
    if (subcategoryRefs.current[subcategoryId]?.current) 
    {
      const stickyHeaderHeight = 220;
      const element = subcategoryRefs.current[subcategoryId].current;
      const y = element.getBoundingClientRect().top + window.pageYOffset - stickyHeaderHeight;
      
      window.scrollTo({top: y, behavior: 'smooth'});
    }
  };

  const handleProductClick = (product) => 
  {
    setMenuOpen(false);
    setSearchOpen(false);
    const position = window.pageYOffset;
    setScrollPosition(position);
    setPreviousView(view);
    
    if (productRefs.current[product.id]?.current) 
    {
      const productPosition = productRefs.current[product.id].current.getBoundingClientRect().top + window.pageYOffset;
      setProductPositions(prev => ({
        ...prev,
        [product.id]: productPosition
      }));
    }
    
    setActiveProduct(product);
    if (!activeCategory || activeCategory.id !== product.category.id) 
    {
      setActiveCategory(product.category);
    }
    if (!activeSubcategory || activeSubcategory.id !== product.subcategory.id) 
    {
      setActiveSubcategory(product.subcategory);
    }
    window.scrollTo(0, 0);
  };

  const handleBackToCategoriesClick = () => 
  {
    setMenuOpen(false);
    setActiveCategory(null);
    setActiveSubcategory(null);
    setActiveProduct(null);
    setView('categories');
    window.scrollTo(0, 0);
  };
  
  const handleBackToSubcategoriesClick = () => 
  {
    setMenuOpen(false);
    setActiveSubcategory(null);
    setActiveProduct(null);
    setView('subcategories');
    window.scrollTo(0, 0);
  };

  const handleBackToProducts = () => 
  {
    setMenuOpen(false);
    const productId = activeProduct.id;
    setActiveProduct(null);
    
    if (previousView === 'all-products') 
    {
      setView('all-products');
    } 
    else 
    {
      setView('products');
    }
    
    setTimeout(() => 
    {
      if (productPositions[productId]) 
      {
        window.scrollTo(0, productPositions[productId] - 100);
      } 
      else if (scrollPosition > 0) 
      {
        window.scrollTo(0, scrollPosition);
      }
    }, 100);
  };

  const handleViewAllProducts = () => 
  {
    setMenuOpen(false);
    setView('all-products');
    if (menuData?.categories && menuData.categories.length > 0) 
    {
      const firstNonHappyHour = menuData.categories.find(c => c.id !== 'happy-hour') || menuData.categories[0];
      setAllViewActiveCategory(firstNonHappyHour);
    }
    window.scrollTo(0, 0);
  };

  const handleSearchClick = () => 
  {
    setSearchOpen(!searchOpen);
    if (!searchOpen) 
    {
      setTimeout(() => 
      {
        if (searchInputRef.current) 
        {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleSearchResult = (result) => 
  {
    setMenuOpen(false);
    setActiveCategory(result.category);
    setActiveSubcategory(result.subcategory);
    setPreviousView(view);
    setView('products');
    setActiveProduct(result);
    setSearchQuery('');
    setSearchOpen(false);
  };

  const stripHTML = (html) => 
  {
    if (typeof html !== 'string') return '';
    return html.replace(/<[^>]*>/g, '');
  };

  const getProductsBySubcategory = () => 
  {
    if (!allViewActiveCategory || !filteredProductsByCategory.length) return [];
    
    const groupedProducts = {};
    
    filteredProductsByCategory.forEach(product => 
    {
      if (!groupedProducts[product.subcategoryId]) 
      {
        groupedProducts[product.subcategoryId] = {
          subcategory: product.subcategory,
          products: []
        };
      }
      
      if (!groupedProducts[product.subcategoryId].products.some(p => p.id === product.id)) 
      {
        groupedProducts[product.subcategoryId].products.push(product);
      }
    });
    
    return Object.values(groupedProducts);
  };

  if (loading) 
  {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-[#200202] to-[#300404] text-white">
        <div className="text-center">
          <div className="h-20 w-48 mx-auto mb-6 relative">
           <img src="/logo.png" alt="TABAC+" className="w-full h-full object-contain" />
         </div>
          <div className="text-sm font-light tracking-wider animate-pulse">Loading menu...</div>
          <div className="mt-4">
            <div className="w-48 h-1 bg-[#410808] rounded-full mx-auto overflow-hidden">
              <div className="w-full h-full bg-gradient-to-r from-[#4d1010] to-[#7a1c1c] animate-pulse rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) 
  {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-[#200202] to-[#300404] text-white">
        <div className="text-center">
          <div className="h-20 w-48 mx-auto mb-6 relative">
           <img src="/logo.png" alt="TABAC+" className="w-full h-full object-contain" />
          </div>
          <div className="text-red-400 mb-4">{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-2 bg-[#3d0a0a] text-white rounded-lg hover:bg-[#4d1010] transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!menuData) 
  {
    return null;
  }

  return (
    <div className="bg-gradient-to-b from-[#200202] via-[#300404] to-[#200202] text-white min-h-screen font-light">
      <a href="#main-content" className="skip-link">Skip to main content</a>

      <header className="p-6 flex justify-between items-center border-b border-[#4d1010] bg-[#200202] sticky top-0 z-40 backdrop-blur-sm bg-opacity-95">
        <button 
          ref={menuButtonRef}
          type="button"
          className="p-2 text-[#c28f8f] hover:text-white transition-colors duration-200 rounded-lg hover:bg-[#3d0a0a]" 
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="sidebar-navigation"
        >
          <Menu size={20} />
        </button>
        
        <button 
          type="button"
          className="text-center group" 
          onClick={handleBackToCategoriesClick}
          aria-label="Go back to all categories"
        >
          <div className="h-14 w-36 mx-auto relative transform transition-transform duration-200 group-hover:scale-105">
            <img src="/logo.png" alt="TABAC+" className="w-full h-full object-contain" />
          </div>
        </button>
        
        <div className="relative" ref={searchRef}>
          <button 
            ref={searchButtonRef}
            type="button"
            className="p-2 text-[#c28f8f] hover:text-white transition-colors duration-200 rounded-lg hover:bg-[#3d0a0a]" 
            onClick={handleSearchClick}
            aria-label={searchOpen ? 'Close search' : 'Open search'}
            aria-expanded={searchOpen}
            aria-controls="menu-search-panel"
          >
            <Search size={20} />
          </button>
          
          <div 
            id="menu-search-panel"
            role="search"
            aria-label="Search menu"
            hidden={!searchOpen}
            inert={!searchOpen ? '' : undefined}
            className={`absolute top-12 right-0 w-80 bg-[#200202] border border-[#4d1010] rounded-xl p-4 z-10 transition-all duration-300 ease-in-out shadow-2xl ${
              searchOpen 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 -translate-y-4 pointer-events-none'
            }`}
          >
            <label htmlFor="menu-search-input" className="sr-only">Search menu</label>
            <input
              id="menu-search-input"
              ref={searchInputRef}
              type="text"
              placeholder="Search our menu..."
              className="w-full bg-[#2c0505] text-white p-3 rounded-lg focus:outline-none border border-[#4d1010] focus:border-[#7a1c1c] transition-colors duration-200"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            
            {searchResults.length > 0 && (
              <div className="mt-4 max-h-96 overflow-y-auto custom-scrollbar">
                {searchResults.map(result => (
                  <button
                    type="button"
                    key={result.id} 
                    className="w-full text-left p-3 hover:bg-[#2c0505] rounded-lg cursor-pointer flex items-center mb-2 transition-all duration-200 group border border-transparent hover:border-[#4d1010]"
                    onClick={() => handleSearchResult(result)}
                    aria-label={`Open ${result.name} in ${result.categoryName}`}
                  >
                    <div className="w-12 h-12 bg-[#2c0505] rounded-lg overflow-hidden mr-3 flex-shrink-0 relative">
                      <LazyImage 
                        src={result.image} 
                        alt={result.name} 
                        className="w-full h-full"
                        width={80}
                        height={80}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white group-hover:text-[#e9d2d2]">{result.name}</div>
                      <div className="text-xs text-[#c28f8f]">{result.categoryName} • {result.subcategoryName}</div>
                      {result.isHappyHour && result.happyHourPrice ? (
                        <div className="text-xs">
                          <span className="text-[#a07676]">{formatPrice(result.price, result.perPerson, true)}</span>
                          {happyHourStatus.isActive && (
                            <span className="text-yellow-400 ml-2">HH: {formatPrice(result.happyHourPrice, result.perPerson, true)}</span>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-[#a07676]">{formatPrice(result.price, result.perPerson, true)}</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {searchQuery.length > 1 && searchResults.length === 0 && (
              <div className="text-center py-8 text-sm text-[#c28f8f]">
                <Search size={32} className="mx-auto mb-2 opacity-50" />
                No results found
              </div>
            )}
          </div>
        </div>
      </header>

      <aside
        id="sidebar-navigation"
        ref={menuRef}
        aria-label="Sidebar navigation"
        aria-hidden={!menuOpen}
        inert={!menuOpen ? '' : undefined}
        className={`fixed top-0 left-0 h-full w-72 bg-[#200202] z-50 transform transition-transform duration-300 ease-in-out ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        } border-r border-[#4d1010] overflow-y-auto custom-scrollbar`}
      >
        <div className="flex justify-end p-4">
          <button type="button" className="text-[#c28f8f] hover:text-white transition-colors duration-200 p-2 rounded-lg hover:bg-[#3d0a0a]" onClick={() => setMenuOpen(false)} aria-label="Close sidebar menu">
            <X size={20} />
          </button>
        </div>
        
        <nav className="p-4" aria-label="Menu sections">
          <div className="h-14 w-36 mx-auto mb-8 relative">
            <img src="/logo.png" alt="TABAC+" className="w-full h-full object-contain" />
          </div>
          
          <button
            ref={sidebarFirstActionRef}
            type="button"
            className={`mb-4 p-3 cursor-pointer rounded-lg transition-colors duration-200 
            ${view === 'categories' ? 'bg-[#3d0a0a] text-white border border-[#4d1010]' : 'text-[#c28f8f] hover:text-white hover:bg-[#2c0505]'}`}
            onClick={handleBackToCategoriesClick}
            aria-current={view === 'categories' ? 'page' : undefined}
          >
            All Categories
          </button>

          <button
            type="button"
            className={`mb-4 p-3 cursor-pointer rounded-lg transition-colors duration-200 
            ${view === 'all-products' ? 'bg-[#3d0a0a] text-white border border-[#4d1010]' : 'text-[#c28f8f] hover:text-white hover:bg-[#2c0505]'}`}
            onClick={handleViewAllProducts}
            aria-current={view === 'all-products' ? 'page' : undefined}
          >
            All Menu Items
          </button>
          
          {menuData.categories?.find(c => c.id === 'happy-hour') && (
            <button
              type="button"
              className={`mb-4 p-3 cursor-pointer rounded-lg transition-colors duration-200 flex items-center
              ${activeCategory?.id === 'happy-hour' ? 'bg-[#3d0a0a] text-white border border-[#4d1010]' : 'text-[#c28f8f] hover:text-white hover:bg-[#2c0505]'}`}
              onClick={() => 
              {
                const happyHourCategory = menuData.categories.find(c => c.id === 'happy-hour');
                if (happyHourCategory) 
                {
                  handleCategoryClick(happyHourCategory);
                  setMenuOpen(false);
                }
              }}
              aria-current={activeCategory?.id === 'happy-hour' ? 'page' : undefined}
            >
              <Sparkles size={16} className="mr-2 text-yellow-400" />
              <span>Happy Hour</span>
              {happyHourStatus.isActive && (
                <span className="ml-auto text-xs bg-[#14532d] text-white px-2 py-0.5 rounded-full">ACTIVE</span>
              )}
            </button>
          )}
          
          {activeCategory && (
            <button
              type="button"
              className={`mb-4 p-3 cursor-pointer rounded-lg transition-colors duration-200 
              ${view === 'subcategories' ? 'bg-[#3d0a0a] text-white border border-[#4d1010]' : 'text-[#c28f8f] hover:text-white hover:bg-[#2c0505]'}`}
              onClick={handleBackToSubcategoriesClick}
              aria-current={view === 'subcategories' ? 'page' : undefined}
            >
              {activeCategory.name}
            </button>
          )}
          
          {activeSubcategory && (
            <button
              type="button"
              className={`mb-4 p-3 cursor-pointer rounded-lg transition-colors duration-200 
              ${view === 'products' ? 'bg-[#3d0a0a] text-white border border-[#4d1010]' : 'text-[#c28f8f] hover:text-white hover:bg-[#2c0505]'}`}
              onClick={() => { setActiveProduct(null); setView('products'); }}
              aria-current={view === 'products' ? 'page' : undefined}
            >
              {activeSubcategory.name}
            </button>
          )}
          
          <div className="mt-8 border-t border-[#4d1010] pt-6">
            <h3 className="text-sm text-[#c28f8f] mb-4 tracking-wider uppercase">Categories</h3>
             
            {menuData.categories?.filter(c => c.id !== 'happy-hour').map(category => (
              <button
                type="button"
                key={category.id} 
                className="w-full text-left mb-4 p-3 cursor-pointer rounded-lg transition-all duration-200 text-[#c28f8f] hover:text-white hover:bg-[#2c0505] border border-transparent hover:border-[#4d1010] group"
                onClick={() => 
                {
                  setActiveCategory(category);
                  setActiveSubcategory(null);
                  setActiveProduct(null);
                  setView('subcategories');
                  setMenuOpen(false);
                }}
                aria-label={`Open ${category.name} category`}
              >
                <div className="flex items-center justify-between">
                  <span>{category.name}</span>
                  <ChevronRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </div>
              </button>
            ))}
          </div>
          
          <div className="mt-8 border-t border-[#4d1010] pt-6 pb-10">
            <div className="text-center text-sm text-[#a07676]">
              <p className="mb-2">© 2025 TABAC+</p>
              <p>An elevated experience</p>
            </div>
          </div>
        </nav>
      </aside>

      <main id="main-content" className="p-6 max-w-7xl mx-auto">

        {view === 'categories' && (
          <>
            <div className="relative mb-12 py-12">
              <div className="absolute inset-0 bg-gradient-to-r from-[#300404] via-transparent to-[#300404] z-0 rounded-2xl"></div>
              <div className="relative z-10 px-8 py-10 text-center">
                <h1 className="text-5xl font-light mb-6 tracking-wider">Discover Our Menu</h1>
                <p className="text-[#e9d2d2] max-w-2xl mx-auto text-lg leading-relaxed">
                  Immerse yourself in the refined world of TABAC+, where every selection is crafted to engage your senses and elevate your experience.
                </p>
                <button 
                  onClick={handleViewAllProducts}
                  className="mt-8 px-8 py-3 bg-[#3d0a0a] hover:bg-[#4d1010] rounded-lg transition-colors duration-200 border border-[#4d1010] hover:border-[#7a1c1c]"
                >
                  Browse All Menu Items
                </button>
              </div>
            </div>
            
            <HappyHourCard />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {menuData.categories?.filter(c => c.id !== 'happy-hour').map((category, index) => (
                <button
                  type="button"
                  key={category.id}
                  className={`w-full text-left border border-[#4d1010] rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-[#7a1c1c] hover:shadow-2xl group bg-[#2c0505] bg-opacity-60 backdrop-blur-sm ${
                    index === 0 ? 'md:col-span-2' : ''
                  }`}
                  onClick={() => handleCategoryClick(category)}
                  aria-label={`Explore ${category.name}`}
                >
                  <div className={`${index === 0 ? 'h-64 lg:h-80' : 'h-48'} overflow-hidden relative`}>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#200202] via-transparent to-transparent opacity-70 z-10"></div>
                    <LazyImage 
                      src={category.image} 
                      alt={category.name} 
                      className="w-full h-full transition-transform duration-500 group-hover:scale-110"
                      width={index === 0 ? 1200 : 600}
                      height={index === 0 ? 600 : 400}
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                      <h2 className={`${index === 0 ? 'text-3xl lg:text-4xl' : 'text-2xl'} tracking-wider mb-2 text-white`}>
                        {category.name}
                      </h2>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className={`${index === 0 ? 'text-base lg:text-lg' : 'text-sm'} text-[#c28f8f] mb-4 leading-relaxed`}>
                      {stripHTML(category.description)}
                    </p>
                    
                    <div className="flex items-center text-sm text-[#a07676] group-hover:text-[#e9d2d2] transition-colors duration-200">
                      <span>Explore {category.name}</span>
                      <ChevronRight size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform duration-200" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            <section className="mt-16 mb-8 border-t border-[#4d1010] pt-12" aria-labelledby="dining-notes-title">
              <h2 id="dining-notes-title" className="text-2xl font-light tracking-wider text-white mb-6">Dining Notes</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
                <div className="bg-[#2c0505] bg-opacity-40 rounded-lg p-6 border border-[#4d1010]">
                  <div className="flex items-start">
                    <div className="mr-3 mt-1">
                      <svg className="w-5 h-5 text-[#c28f8f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-[#e9d2d2] mb-2">Group Dining</h3>
                      <p className="text-xs text-[#a07676] leading-relaxed">
                        For parties of 5 or more, an 18% service charge is automatically applied to the final bill, For parties of 8 or more, a 22% service charge is automatically applied to the final bill.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-[#3d0a0a] to-[#2c0505] rounded-lg p-6 border border-[#7a1c1c] shadow-lg shadow-black/20">
                  <div className="flex items-start">
                    <div className="mr-3 mt-1">
                      <svg className="w-5 h-5 text-[#e9d2d2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white mb-2 tracking-wide">Service Charge</h3>
                      <p className="text-sm text-[#e9d2d2] leading-relaxed">
                        A 4% service charge is included on all other checks and shared among our team. Additional gratuity is optional and entirely at your discretion.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-[#2c0505] bg-opacity-40 rounded-lg p-6 border border-[#4d1010]">
                  <div className="flex items-start">
                    <div className="mr-3 mt-1">
                      <svg className="w-5 h-5 text-[#c28f8f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-[#e9d2d2] mb-2">Hookah Sharing</h3>
                      <p className="text-xs text-[#a07676] leading-relaxed">
                        To preserve the integrity of the flavor and ensure optimal heat management, each hookah is thoughtfully prepared for up to 3 guests. Sharing among larger parties may affect quality and can result in harsh or burnt taste.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#2c0505] bg-opacity-40 rounded-lg p-6 border border-[#4d1010]">
                    <div className="flex items-start">
                        <div className="mr-3 mt-1">
                            <svg className="w-5 h-5 text-[#c28f8f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-[#e9d2d2] mb-2">Allergen Notice</h3>
                            <p className="text-xs text-[#a07676] leading-relaxed">
                                Your well-being is our priority. Our dishes may contain dairy, gluten, nuts, or other common allergens. Please inform our staff of any allergies before placing your order.
                            </p>
                        </div>
                    </div>
                </div>			
              </div>
            </section>
          </>
        )} 
        
        {view === 'subcategories' && activeCategory && (
          <>
            <button 
              className="flex items-center text-[#c28f8f] mb-8 group hover:text-white transition-colors duration-200"
              onClick={handleBackToCategoriesClick}
            >
              <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform duration-200" />
              <span className="ml-1">Back to All Categories</span>
            </button>
            
            {activeCategory.id === 'happy-hour' && <HappyHourCard />}
            
            <div className="mb-10">
              <h1 className="text-4xl font-light mb-4 tracking-wider flex items-center">
                {activeCategory.id === 'happy-hour' && <Sparkles className="mr-3 text-yellow-400" size={32} />}
                {activeCategory.name}
              </h1>
              <p className="text-[#c28f8f] text-lg">{stripHTML(activeCategory.description)}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {activeCategory.subcategories?.map(subcategory => (
                <button
                  type="button"
                  key={subcategory.id}
                  className="w-full text-left border border-[#4d1010] rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-[#7a1c1c] hover:shadow-2xl group bg-[#2c0505] bg-opacity-60"
                  onClick={() => handleSubcategoryClick(subcategory)}
                  aria-label={`View ${subcategory.name}`}
                >
                  <div className="h-48 overflow-hidden relative">
                    <div className="absolute inset-0 bg-gradient-to-t from-[#200202] via-transparent to-transparent opacity-70 z-10"></div>
                    <LazyImage 
                      src={subcategory.image} 
                      alt={subcategory.name} 
                      className="w-full h-full transition-transform duration-500 group-hover:scale-110"
                      width={600}
                      height={400}
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                      <h2 className="text-xl tracking-wider text-white">{subcategory.name}</h2>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className="text-sm text-[#c28f8f] mb-4">{stripHTML(subcategory.description)}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-[#a07676] group-hover:text-[#e9d2d2] transition-colors duration-200">
                        <span>View {subcategory.name}</span>
                        <ChevronRight size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform duration-200" />
                      </div>
                      <span className="text-xs text-[#a07676]">
                        {subcategory.products?.length || 0} items
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {view === 'all-products' && !activeProduct && (
          <>
            <div className="mb-2">
              <h1 className="text-3xl font-light mb-4 tracking-wider">All Menu Items</h1>
            </div>
            
            <HappyHourCard compact={true} />
               
            <div className="sticky top-[96px] z-30 bg-[#200202] py-4 border-b border-[#4d1010] mb-6 mt-4">
              <div className="flex overflow-x-auto pb-2 custom-scrollbar">
                {menuData.categories?.filter(c => c.id !== 'happy-hour').map(category => (
                  <button
                    type="button"
                    key={category.id}
                    className={`whitespace-nowrap cursor-pointer px-5 py-2 rounded-full transition-all duration-200 border flex-shrink-0 mr-2 ${
                      allViewActiveCategory && allViewActiveCategory.id === category.id
                      ? 'bg-[#3d0a0a] text-white border-[#7a1c1c]'
                      : 'text-[#c28f8f] hover:text-white border-[#4d1010] hover:border-[#7a1c1c] hover:bg-[#2c0505]'
                    }`}
                    onClick={() => handleAllViewCategoryClick(category)}
                    aria-pressed={allViewActiveCategory && allViewActiveCategory.id === category.id}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
              
              {allViewActiveCategory && allViewActiveCategory.id !== 'happy-hour' && (
                <div className="flex overflow-x-auto mt-4 custom-scrollbar">
                  {allViewActiveCategory.subcategories?.map(subcategory => (
                    <button
                      type="button"
                      key={subcategory.id}
                      className="text-sm cursor-pointer px-3 py-1 rounded-full border border-[#4d1010] text-[#c28f8f] hover:text-white hover:bg-[#2c0505] transition-all duration-200 flex-shrink-0 mr-2"
                      onClick={() => scrollToSubcategory(subcategory.id)}
                      aria-label={`Jump to ${subcategory.name}`}
                    >
                      {subcategory.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              {getProductsBySubcategory().map(group => (
                <div key={group.subcategory.id} ref={subcategoryRefs.current[group.subcategory.id]} className="mb-12 pt-4">
                  <h2 className="text-2xl font-light tracking-wider mb-6 pb-2 border-b border-[#4d1010]">
                    {group.subcategory.name}
                  </h2>
                  
                  <div className="space-y-4">
                    {group.products.map((product, index) => (
                      <button
                        type="button"
                        key={`${product.id}-${index}`}
                        className="w-full text-left border border-[#4d1010] rounded-xl p-4 cursor-pointer transition-all duration-300 hover:border-[#7a1c1c] hover:bg-[#2c0505] hover:bg-opacity-30 group"
                        onClick={() => handleProductClick(product)}
                        ref={productRefs.current[product.id]}
                        aria-label={`View details for ${product.name}`}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-20 h-20 bg-[#2c0505] rounded-lg overflow-hidden flex-shrink-0">
                            <LazyImage 
                              src={product.image} 
                              alt={product.name} 
                              className="w-full h-full"
                              width={80}
                              height={80}
                              objectFit="cover"
                            />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="text-lg font-medium text-white group-hover:text-[#e9d2d2] transition-colors duration-200 flex items-center">
                                  {product.name}
                                  {product.isHappyHour && (
                                    <Sparkles size={14} className="ml-2 text-yellow-400" />
                                  )}
                                </h3>
                                <p className="text-sm text-[#a07676] line-clamp-2 mt-1">
                                  {stripHTML(product.description)}
                                </p>
                              </div>
                              
                              <div className="flex flex-col items-end space-y-2 ml-4">
                                <ProductPriceDisplay product={product} />
                                {product.featured && (
                                  <span className="px-2 py-1 bg-[#3d0a0a] text-xs rounded-full">
                                    Featured
                                  </span>
                                )}
                                <div className="flex items-center text-xs text-[#a07676]">
                                  <Eye size={12} className="mr-1" />
                                  <span>View Details</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <div 
                ref={loaderRef}
                className="flex justify-center py-8"
              >
                <div className="text-[#c28f8f] text-sm">
                  Loading more items...
                </div>
              </div>
            )}
          </>
        )}

        {view === 'products' && activeCategory && activeSubcategory && !activeProduct && (
          <>
            <div className="mb-8">
              <button 
                className="flex items-center text-[#c28f8f] mb-8 group hover:text-white transition-colors duration-200"
                onClick={handleBackToSubcategoriesClick}
              >
                <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform duration-200" />
                <span className="ml-1">Back to {activeCategory.name}</span>
              </button>
              
              {activeCategory.id === 'happy-hour' && <HappyHourCard />}
              
              <div className="mb-6">
                <h1 className="text-4xl font-light mb-4 tracking-wider">{activeSubcategory.name}</h1>
                <p className="text-[#c28f8f] text-lg">{stripHTML(activeSubcategory.description)}</p>
              </div>
              
              <div className="flex overflow-x-auto pb-2 mb-8 custom-scrollbar">
                {activeCategory.subcategories?.map(subcategory => (
                  <button
                    type="button"
                    key={subcategory.id}
                    className={`whitespace-nowrap cursor-pointer px-6 py-3 rounded-full transition-all duration-200 border flex-shrink-0 mr-2 ${
                      activeSubcategory.id === subcategory.id
                      ? 'bg-[#3d0a0a] text-white border-[#7a1c1c]'
                      : 'text-[#c28f8f] hover:text-white border-[#4d1010] hover:border-[#7a1c1c] hover:bg-[#2c0505]'
                    }`}
                    onClick={() => setActiveSubcategory(subcategory)}
                    aria-pressed={activeSubcategory.id === subcategory.id}
                  >
                    {subcategory.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="space-y-4">
              {visibleProducts.map((product, index) => (
                <button
                  type="button"
                  key={`${product.id}-${index}`}
                  className="w-full text-left border border-[#4d1010] rounded-xl p-4 cursor-pointer transition-all duration-300 hover:border-[#7a1c1c] hover:bg-[#2c0505] hover:bg-opacity-30 group"
                  onClick={() => handleProductClick(product)}
                  ref={productRefs.current[product.id]}
                  aria-label={`View details for ${product.name}`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-20 h-20 bg-[#2c0505] rounded-lg overflow-hidden flex-shrink-0">
                      <LazyImage 
                        src={product.image} 
                        alt={product.name} 
                        className="w-full h-full"
                        width={80}
                        height={80}
                        objectFit="cover"
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-white group-hover:text-[#e9d2d2] transition-colors duration-200 flex items-center">
                            {product.name}
                            {product.isHappyHour && (
                              <Sparkles size={14} className="ml-2 text-yellow-400" />
                            )}
                          </h3>
                          <p className="text-sm text-[#a07676] line-clamp-2 mt-1">
                            {stripHTML(product.description)}
                          </p>
                        </div>
                        
                        <div className="flex flex-col items-end space-y-2 ml-4">
                          <ProductPriceDisplay product={product} />
                          {product.featured && (
                            <span className="px-2 py-1 bg-[#3d0a0a] text-xs rounded-full">
                              Featured
                            </span>
                          )}
                          <div className="flex items-center text-xs text-[#a07676]">
                            <Eye size={12} className="mr-1" />
                            <span>View Details</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            {hasMore && (
              <div 
                ref={loaderRef}
                className="flex justify-center py-8"
              >
                <div className="text-[#c28f8f] text-sm">
                  Loading more items...
                </div>
              </div>
            )}
            
            {(() => 
            {
              const currentIndex = activeCategory.subcategories?.findIndex(sub => sub.id === activeSubcategory.id) || 0;
              const nextSubcategory = activeCategory.subcategories?.[currentIndex + 1];
              const prevSubcategory = activeCategory.subcategories?.[currentIndex - 1];
              
              return (
                <div className="flex justify-between mt-12 pt-8 border-t border-[#4d1010]">
                  {prevSubcategory && (
                    <button 
                      onClick={() => handleSubcategoryClick(prevSubcategory)}
                      className="flex items-center text-[#c28f8f] hover:text-white transition-colors duration-200 group"
                    >
                      <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-200" />
                      <span className="ml-1">Previous: {prevSubcategory.name}</span>
                    </button>
                  )}
                  
                  {nextSubcategory && (
                    <button 
                      onClick={() => handleSubcategoryClick(nextSubcategory)}
                      className="flex items-center text-[#c28f8f] hover:text-white transition-colors duration-200 group ml-auto"
                    >
                      <span className="mr-1">Next: {nextSubcategory.name}</span>
                      <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
                    </button>
                  )}
                </div>
              );
            })()}
          </>
        )}

        {activeProduct && (
          <div className="pb-8">
            <button 
              className="flex items-center text-[#c28f8f] mb-8 group hover:text-white transition-colors duration-200"
              onClick={handleBackToProducts}
            >
              <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform duration-200" />
              <span className="ml-1">
                {previousView === 'all-products' 
                  ? 'Back to All Menu Items' 
                  : `Back to ${activeSubcategory?.name || 'Products'}`}
              </span>
            </button>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="flex items-center justify-center">
                {activeProduct.image ? (
                  <img 
                    src={activeProduct.image} 
                    alt={activeProduct.name} 
                    className="max-w-full rounded-xl"
                  />
                ) : (
                  <div className="h-64 w-full flex items-center justify-center text-[#a07676] rounded-xl border border-[#4d1010]">
                    <div className="text-center">
                      <div className="text-4xl mb-2">📷</div>
                      <p>No image available</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h1 className="text-4xl font-light tracking-wider mb-2 flex items-center">
                      {activeProduct.name}
                      {activeProduct.isHappyHour && (
                        <Sparkles size={24} className="ml-3 text-yellow-400" />
                      )}
                    </h1>
                    {activeProduct.featured && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-[#3d0a0a] text-white">
                        <Tag size={12} className="mr-1" />
                        Featured
                      </span>
                    )}
                  </div>
                  {activeProduct.isHappyHour && activeProduct.happyHourPrice ? (
                    <div className="text-right">
                      {happyHourStatus.isActive ? (
                        <>
                          <div className="text-lg line-through text-[#a07676]">{formatPrice(activeProduct.price, activeProduct.perPerson, true)}</div>
                          <div className="text-3xl font-light text-yellow-400">{formatPrice(activeProduct.happyHourPrice, activeProduct.perPerson)}</div>
                          <div className="text-xs bg-[#14532d] text-white px-2 py-1 rounded-full mt-2">HAPPY HOUR</div>
                        </>
                      ) : (
                        <>
                          <div className="text-3xl font-light">{formatPrice(activeProduct.price, activeProduct.perPerson)}</div>
                          <div className="text-sm text-[#c28f8f] mt-1">Happy Hour: {formatPrice(activeProduct.happyHourPrice, activeProduct.perPerson, true)}</div>
                        </>
                      )}
                    </div>
                  ) : (
                    <span className="text-3xl font-light">{formatPrice(activeProduct.price, activeProduct.perPerson)}</span>
                  )}
                </div>
                
                <div className="text-[#e9d2d2] mb-8 leading-relaxed text-lg">
                  {typeof activeProduct.description === 'string' ? (
                    <div dangerouslySetInnerHTML={{ __html: activeProduct.description }} />
                  ) : (
                    activeProduct.description
                  )}
                </div>
                
                {activeProduct.isHappyHour && !happyHourStatus.isActive && (
                  <div className="border border-[#4d1010] rounded-xl p-4 bg-[#2c0505] bg-opacity-40 mb-6">
                    <div className="flex items-center text-[#c28f8f]">
                      <Clock size={20} className="mr-2" />
                      <span>Happy Hour: {happyHourStatus.displayText}</span>
                    </div>
                    {happyHourStatus.nextStart && (
                      <div className="text-sm text-[#a07676] mt-2">
                        Next Happy Hour: {happyHourStatus.nextStart}
                      </div>
                    )}
                  </div>
                )}
                
                {activeProduct.emotions && Object.keys(activeProduct.emotions).length > 0 && (
                  <div className="border border-[#4d1010] rounded-xl p-6 bg-[#2c0505] bg-opacity-40 mb-6">
                    <h3 className="mb-6 text-xl tracking-wider">Experience</h3>
                    
                    <div className="space-y-4">
                      {Object.entries(activeProduct.emotions).map(([key, value]) => (
                        <div key={key} className="grid grid-cols-3 gap-4 text-sm">
                          <div className="text-[#c28f8f] capitalize">{key}</div>
                          <div className="col-span-2 text-[#e9d2d2]">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <style>{`
        .skip-link {
          position: absolute;
          top: 16px;
          left: 16px;
          z-index: 100;
          background: #ffffff;
          color: #200202;
          padding: 12px 16px;
          border-radius: 8px;
          text-decoration: none;
          transform: translateY(-200%);
          transition: transform 0.2s ease;
        }
        .skip-link:focus,
        .skip-link:focus-visible {
          transform: translateY(0);
        }
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
        button:focus-visible,
        input:focus-visible,
        a:focus-visible {
          outline: 3px solid #e9d2d2;
          outline-offset: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #2c0505;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #4d1010;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #7a1c1c;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default TabacMenuInterface;
